import { forwardRef, useCallback, type KeyboardEvent } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';

/**
 * Props interface for SearchBar component
 */
export interface SearchBarProps {
  /** Placeholder text for the input */
  placeholder?: string;
  /** Current search value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Callback when search is submitted (Enter key or button click) */
  onSearch?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Whether font enlargement mode is active (accessibility) */
  isFontEnlarged?: boolean;
}

/**
 * SearchBar - Search input with button for service directory
 *
 * A styled search bar with an input field and search button.
 * Supports both controlled input and search submission.
 *
 * @example
 * ```tsx
 * import { SearchBar } from '@/components';
 *
 * const [query, setQuery] = useState('');
 *
 * <SearchBar
 *   placeholder="Search services..."
 *   value={query}
 *   onChange={setQuery}
 *   onSearch={() => console.log('Searching:', query)}
 * />
 * ```
 */
export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      placeholder = 'Search...',
      value,
      onChange,
      onSearch,
      className,
      isFontEnlarged = false,
    },
    ref
  ) => {
    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
      },
      [onChange]
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && onSearch) {
          event.preventDefault();
          onSearch();
        }
      },
      [onSearch]
    );

    const handleButtonClick = useCallback(() => {
      onSearch?.();
    }, [onSearch]);

    return (
      <div
        className={cn('flex items-stretch w-full', className)}
        role="search"
      >
        {/* Input field */}
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            'min-w-0 flex-1 px-3 py-2',
            isFontEnlarged ? 'h-12' : 'h-10',
            typography.body,
            'bg-white text-foreground',
            'border border-r-0 border-[var(--color-input-border)]',
            'placeholder:text-[var(--color-text-secondary)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-brand)] focus:ring-inset',
            'transition-colors duration-150',
            'motion-reduce:transition-none'
          )}
          style={isFontEnlarged ? { fontSize: '24px' } : undefined}
          aria-label="Search services"
        />

        {/* Search button */}
        <button
          type="button"
          onClick={handleButtonClick}
          className={cn(
            'flex items-center justify-center shrink-0',
            isFontEnlarged ? 'w-[75px] h-12' : 'w-[75px] h-10',
            'bg-[var(--color-primary-brand)] text-white',
            'hover:bg-[var(--color-primary-brand-hover)]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
            'transition-colors duration-150',
            'motion-reduce:transition-none',
            'cursor-pointer'
          )}
          aria-label="Submit search"
        >
          <Search className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    );
  }
);

SearchBar.displayName = 'SearchBar';
